import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useLocalization } from '@/contexts/LocalizationContext'

type KnownStatus =
  | 'SCHEDULED'
  | 'PENDING'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'UPCOMING'
  | 'IN_PROGRESS'
  | 'ASSIGNED'
  | 'ACCEPTED'
  | 'CREATED'
  | 'DONE'

interface StatusBadgeProps {
  status: string
  className?: string
}

const statusConfig: Record<KnownStatus, { className: string }> = {
  SCHEDULED: {
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  PENDING: {
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  COMPLETED: {
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  CANCELLED: {
    className: 'bg-red-100 text-red-800 border-red-200',
  },
  UPCOMING: {
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  IN_PROGRESS: {
    className: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  ASSIGNED: {
    className: 'bg-sky-100 text-sky-800 border-sky-200',
  },
  ACCEPTED: {
    className: 'bg-teal-100 text-teal-800 border-teal-200',
  },
  CREATED: {
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  DONE: {
    className: 'bg-green-100 text-green-800 border-green-200',
  },
}

const defaultStyle = 'bg-gray-100 text-gray-700 border-gray-200'

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const { t } = useLocalization()
  const key = (status || '').toUpperCase().trim().replace(/\s+/g, '_') as KnownStatus
  const config = statusConfig[key]

  const statusLabels: Record<KnownStatus, string> = {
    SCHEDULED: t('status.scheduled'),
    PENDING: t('status.pending'),
    COMPLETED: t('status.completed'),
    CANCELLED: t('status.cancelled'),
    UPCOMING: t('status.upcoming'),
    IN_PROGRESS: t('status.inProgress'),
    ASSIGNED: t('status.assigned'),
    ACCEPTED: t('status.accepted'),
    CREATED: t('status.created'),
    DONE: t('status.done'),
  }

  const label = statusLabels[key] ?? (status || '—')
  const style = config?.className ?? defaultStyle

  return (
    <Badge
      variant="outline"
      className={cn('border font-medium', style, className)}
    >
      {label}
    </Badge>
  )
}
